// src/api/circlesApi.ts
//
// RTK Query endpoints for the CRM Circles backend
// (`server/src/routes/circles.ts`). All endpoints tag under `Circle` — the
// `useCircleRealtime` hook uses the same tag names to invalidate on
// server-emitted CIRCLE_* realtime events.

import { baseApi } from './baseApi';
import type {
  AddCircleCentralMemberRequest,
  BulkAddMembersResponse,
  BulkRemoveMembersRequest,
  CentralTeamSummary,
  Circle,
  CircleCentralCandidatesResponse,
  CircleMember,
  CircleMemberInput,
  CirclePeopleResponse,
  ContactFilter,
  CreateCircleRequest,
  IntegrationConnection,
  SmartCirclePreview,
  ListCirclesParams,
  UpdateCircleCentralMemberRequest,
  UpdateCircleRequest,
  WorkspaceCentralTeamsResponse,
} from '../types/api';

/**
 * Phase 12.7 — Circle → People per-member provider pin channel domain.
 */
export type MemberAssignmentChannel =
  | 'voice'
  | 'sms'
  | 'email'
  | 'whatsapp'
  | 'voice_ai';

/**
 * Safe view of a member's per-channel provider pin returned by the
 * People-tab bulk read. NEVER carries credentials.
 */
export interface MemberProviderAssignment {
  channelType: MemberAssignmentChannel;
  outboundProviderId: string;
  providerDisplayName: string | null;
  providerAppKey: string | null;
  providerStatus: string | null;
}

/**
 * Raw row shape returned by PUT — mirrors Outbound's assignment row.
 */
export interface MemberProviderAssignmentRow {
  id: string;
  workspaceId: string;
  circleId: string;
  userId: string;
  channelType: MemberAssignmentChannel;
  workspaceProviderId: string;
  createdByUserId: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Phase A5.6 — a Central ProviderAccessGrant row.
 *
 * NOT a pin. This says the member may USE the provider across suite
 * apps; `MemberProviderAssignment` above says which provider this Circle
 * prefers. The two are stored in different systems and neither implies
 * the other. `id` is required for revoke — pins have no such handle
 * because they are replaced, not revoked.
 */
export interface ProviderAccessGrantRow {
  id: string;
  workspaceId: string;
  userId: string;
  workspaceProviderId: string;
  channelType: string;
  grantingTeamId: string | null;
  grantedByUserId: string | null;
  status: 'active' | 'revoked';
  grantedAt: string | null;
  revokedAt: string | null;
}

/**
 * Phase 12.4 — Circle ↔ Outbound Provider affinity response shape.
 *
 * `integrations` mirrors the safe shape returned by
 * `GET /api/integrations`; nothing new is exposed. `affinity` carries
 * the explicit affinity set for backward-compat rendering
 * (`hasExplicitAffinity === false` → full workspace catalog, show the
 * onboarding copy).
 */
export interface CircleProvidersResponse {
  integrations: IntegrationConnection[];
  affinity: {
    outboundProviderIds: string[];
    hasExplicitAffinity: boolean;
  };
}

const LIST_ID = 'LIST' as const;

export const circlesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // ---- Reads ---------------------------------------------------------
    getCircles: builder.query<Circle[], ListCirclesParams>({
      query: (params) => {
        const search = new URLSearchParams();
        if (params.workspaceId) search.append('workspaceId', params.workspaceId);
        if (params.q) search.append('q', params.q);
        if (params.type) search.append('type', params.type);
        if (params.includeArchived) search.append('includeArchived', '1');
        if (params.ownerUserId) search.append('ownerUserId', params.ownerUserId);
        if (params.parentCircleId !== undefined) {
          search.append(
            'parentCircleId',
            params.parentCircleId === null ? 'null' : params.parentCircleId,
          );
        }
        if (params.limit != null) search.append('limit', String(params.limit));
        if (params.offset != null) search.append('offset', String(params.offset));
        const qs = search.toString();
        return `/circles${qs ? `?${qs}` : ''}`;
      },
      providesTags: (result) =>
        result
          ? [
              { type: 'Circle' as const, id: LIST_ID },
              ...result.map((c) => ({ type: 'Circle' as const, id: c.id })),
            ]
          : [{ type: 'Circle' as const, id: LIST_ID }],
    }),

    getCircle: builder.query<Circle, string>({
      query: (id) => `/circles/${id}`,
      providesTags: (_res, _err, id) => [{ type: 'Circle' as const, id }],
    }),

    getCircleMemberHistory: builder.query<CircleMember[], string>({
      query: (circleId) => `/circles/${circleId}/members/history`,
      providesTags: (_res, _err, id) => [
        { type: 'Circle' as const, id: `${id}-history` },
      ],
    }),

    // Source-discriminated People roster. Central-backed circles
    // (Department/Team + centralTeamId) return `source: 'central_team'`
    // with a read-only list of Central Team members. All other circles
    // return `source: 'crm_contacts'` with the local CircleMember rows.
    getCirclePeople: builder.query<CirclePeopleResponse, string>({
      query: (circleId) => `/circles/${circleId}/people`,
      providesTags: (_res, _err, id) => [
        { type: 'Circle' as const, id: `${id}-people` },
      ],
    }),

    // Add-member picker source for linked Central-backed Circles.
    getCirclePeopleCandidates: builder.query<CircleCentralCandidatesResponse, string>({
      query: (circleId) => `/circles/${circleId}/people/candidates`,
      providesTags: (_res, _err, id) => [
        { type: 'Circle' as const, id: `${id}-people-candidates` },
      ],
    }),

    addCircleCentralMember: builder.mutation<
      { ok: true; membership: unknown },
      { circleId: string; body: AddCircleCentralMemberRequest }
    >({
      query: ({ circleId, body }) => ({
        url: `/circles/${circleId}/people`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_res, _err, arg) => [
        { type: 'Circle', id: `${arg.circleId}-people` },
        { type: 'Circle', id: `${arg.circleId}-people-candidates` },
      ],
    }),

    updateCircleCentralMember: builder.mutation<
      { ok: true; membership: unknown },
      { circleId: string; membershipId: string; body: UpdateCircleCentralMemberRequest }
    >({
      query: ({ circleId, membershipId, body }) => ({
        url: `/circles/${circleId}/people/${membershipId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_res, _err, arg) => [
        { type: 'Circle', id: `${arg.circleId}-people` },
      ],
    }),

    removeCircleCentralMember: builder.mutation<
      { ok: true; forced: boolean },
      { circleId: string; membershipId: string }
    >({
      query: ({ circleId, membershipId }) => ({
        url: `/circles/${circleId}/people/${membershipId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_res, _err, arg) => [
        { type: 'Circle', id: `${arg.circleId}-people` },
        { type: 'Circle', id: `${arg.circleId}-people-candidates` },
      ],
    }),

    // ---- Central Team picker source -----------------------------------
    // Metadata-only summary from Central for the Team-linkage select.
    // Central remains the source of truth — this response is not
    // persisted anywhere on the CRM side.
    //
    // Phase 12: the picker on New Circle / Link Central Team passes
    // `{ onlyHeaded: true }` to receive only Teams where the caller is
    // an active Head. Existing consumers that need the full list omit
    // the arg. Backend independently re-validates on write.
    getWorkspaceCentralTeams: builder.query<
      WorkspaceCentralTeamsResponse,
      { onlyHeaded?: boolean } | void
    >({
      query: (arg) => {
        const onlyHeaded = arg && arg.onlyHeaded ? '?onlyHeaded=true' : '';
        return `/circles/central-teams${onlyHeaded}`;
      },
      // Distinct cache buckets for full-list vs Heads-only so switching
      // between the New Circle picker and other consumers does not
      // clobber the shared cache.
      providesTags: (_res, _err, arg) => [
        {
          type: 'Circle' as const,
          id: arg && arg.onlyHeaded ? 'CENTRAL_TEAMS_HEADED' : 'CENTRAL_TEAMS',
        },
      ],
    }),

    // Smart Circle rule preview — caller-scoped { count, sample }. A
    // mutation only because the rules travel as a JSON body; it writes
    // nothing and invalidates nothing.
    previewSmartCircle: builder.mutation<SmartCirclePreview, { rules: ContactFilter }>({
      query: (body) => ({ url: '/circles/smart-preview', method: 'POST', body }),
    }),

    // ---- Writes --------------------------------------------------------
    createCircle: builder.mutation<Circle, CreateCircleRequest>({
      query: (body) => ({ url: '/circles', method: 'POST', body }),
      invalidatesTags: [{ type: 'Circle', id: LIST_ID }],
    }),

    updateCircle: builder.mutation<
      Circle,
      { id: string; data: UpdateCircleRequest }
    >({
      query: ({ id, data }) => ({
        url: `/circles/${id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_res, _err, arg) => [
        { type: 'Circle', id: arg.id },
        { type: 'Circle', id: LIST_ID },
      ],
    }),

    // ---- Lifecycle -----------------------------------------------------
    archiveCircle: builder.mutation<Circle, string>({
      query: (id) => ({ url: `/circles/${id}/archive`, method: 'POST' }),
      invalidatesTags: (_res, _err, id) => [
        { type: 'Circle', id },
        { type: 'Circle', id: LIST_ID },
      ],
    }),

    restoreCircle: builder.mutation<Circle, string>({
      query: (id) => ({ url: `/circles/${id}/restore`, method: 'POST' }),
      invalidatesTags: (_res, _err, id) => [
        { type: 'Circle', id },
        { type: 'Circle', id: LIST_ID },
      ],
    }),

    deleteCircle: builder.mutation<
      { success: boolean; softDelete?: boolean },
      string
    >({
      query: (id) => ({ url: `/circles/${id}`, method: 'DELETE' }),
      invalidatesTags: (_res, _err, id) => [
        { type: 'Circle', id },
        { type: 'Circle', id: LIST_ID },
        'Referral',
      ],
    }),

    // ---- Membership ----------------------------------------------------
    addCircleMembers: builder.mutation<
      BulkAddMembersResponse,
      { circleId: string; members: CircleMemberInput[] }
    >({
      query: ({ circleId, members }) => ({
        url: `/circles/${circleId}/members`,
        method: 'POST',
        body: { members },
      }),
      invalidatesTags: (_res, _err, arg) => [
        { type: 'Circle', id: arg.circleId },
        { type: 'Circle', id: `${arg.circleId}-history` },
        { type: 'Circle', id: `${arg.circleId}-people` },
        { type: 'Circle', id: LIST_ID },
        { type: 'Contact', id: 'LIST' },
      ],
    }),

    removeCircleMember: builder.mutation<
      { success: boolean },
      { circleId: string; memberId: string }
    >({
      query: ({ circleId, memberId }) => ({
        url: `/circles/${circleId}/members/${memberId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_res, _err, arg) => [
        { type: 'Circle', id: arg.circleId },
        { type: 'Circle', id: `${arg.circleId}-history` },
        { type: 'Circle', id: `${arg.circleId}-people` },
        { type: 'Circle', id: LIST_ID },
        { type: 'Contact', id: 'LIST' },
      ],
    }),

    bulkRemoveCircleMembers: builder.mutation<
      { success: boolean; removed: number },
      { circleId: string } & BulkRemoveMembersRequest
    >({
      query: ({ circleId, identifiers }) => ({
        url: `/circles/${circleId}/members:bulkRemove`,
        method: 'POST',
        body: { identifiers },
      }),
      invalidatesTags: (_res, _err, arg) => [
        { type: 'Circle', id: arg.circleId },
        { type: 'Circle', id: `${arg.circleId}-history` },
        { type: 'Circle', id: `${arg.circleId}-people` },
        { type: 'Circle', id: LIST_ID },
        { type: 'Contact', id: 'LIST' },
      ],
    }),

    // ---- Phase 12.4 — Circle ↔ Outbound Provider affinity ------------------
    //
    // GET the effective provider list for a Circle. Backward-compat: a
    // Circle with zero affinity rows sees the FULL workspace Outbound
    // catalog. The `affinity.hasExplicitAffinity` flag drives the
    // full-catalog explainer copy in the UI.
    getCircleProviders: builder.query<CircleProvidersResponse, string>({
      query: (circleId) => `/circles/${circleId}/providers`,
      providesTags: (_res, _err, id) => [
        { type: 'Circle' as const, id: `${id}-providers` },
      ],
    }),

    // Head-only. The backend gate (authorizeCircleWrite → Head-of-Team)
    // is authoritative — this mutation is displayed only to Heads on the
    // web tab, but a non-Head who fires the request directly still 403s.
    addCircleProvider: builder.mutation<
      {
        id: string;
        workspaceId: string;
        circleId: string;
        outboundProviderId: string;
        createdAt: string;
      },
      { circleId: string; outboundProviderId: string }
    >({
      query: ({ circleId, outboundProviderId }) => ({
        url: `/circles/${circleId}/providers`,
        method: 'POST',
        body: { outboundProviderId },
      }),
      invalidatesTags: (_res, _err, arg) => [
        { type: 'Circle', id: `${arg.circleId}-providers` },
      ],
    }),

    removeCircleProvider: builder.mutation<
      { success: boolean },
      { circleId: string; outboundProviderId: string }
    >({
      query: ({ circleId, outboundProviderId }) => ({
        url: `/circles/${circleId}/providers/${encodeURIComponent(outboundProviderId)}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_res, _err, arg) => [
        { type: 'Circle', id: `${arg.circleId}-providers` },
      ],
    }),

    // ---- Phase 12.7 — Circle → People per-member provider pin -------------
    //
    // Bulk read for the People-tab render. Read-only; workspace-scoped.
    // Returns { assignmentsByUserId: { [centralUserId]: MemberAssignmentView[] } }
    // where MemberAssignmentView carries the safe provider display
    // fields (name, appKey, status) — never credentials.
    getCirclePeopleProviders: builder.query<
      { assignmentsByUserId: Record<string, MemberProviderAssignment[]> },
      { circleId: string; userIds?: string[] }
    >({
      query: ({ circleId, userIds }) => {
        const qs = userIds && userIds.length > 0 ? `?userIds=${encodeURIComponent(userIds.join(','))}` : '';
        return `/circles/${circleId}/people/providers${qs}`;
      },
      providesTags: (_res, _err, arg) => [
        { type: 'Circle' as const, id: `${arg.circleId}-people-providers` },
      ],
    }),

    // -----------------------------------------------------------------
    // Phase A5.6 — PROVIDER ACCESS (Central ProviderAccessGrant).
    //
    // DISTINCT FROM THE PIN ENDPOINTS ABOVE. Pins live in Outbound
    // (`workspace_provider_user_assignments`) and mean "prefer this
    // provider inside this Circle". Grants live in Central
    // (`provider_access_grants`) and mean "may USE this provider across
    // suite apps". Separate cache tags on purpose: granting must never
    // invalidate pin queries, or the two would appear coupled in the UI.
    // -----------------------------------------------------------------

    // Self read. Safe for every member — the server derives the user
    // from the Central JWT `sub`, so this cannot read anyone else.
    getMyProviderAccess: builder.query<
      {
        workspaceId: string;
        entitled: Array<{ workspaceProviderId: string; channelType: string }>;
        policy: 'legacy' | 'strict' | null;
      },
      { circleId: string; channelType?: string }
    >({
      query: ({ circleId, channelType }) => {
        const qs = channelType ? `?channelType=${encodeURIComponent(channelType)}` : '';
        return `/circles/${circleId}/provider-access/me${qs}`;
      },
      providesTags: (_res, _err, arg) => [
        { type: 'Circle' as const, id: `${arg.circleId}-provider-access-me` },
      ],
    }),

    // Head-facing cross-user read. Returns ACTIVE grant rows including
    // `id`, which the revoke control needs. Central 403s a non-Head.
    getMemberProviderAccess: builder.query<
      {
        workspaceId: string;
        userId: string;
        grants: ProviderAccessGrantRow[];
      },
      { circleId: string; memberUserId: string; channelType?: string }
    >({
      query: ({ circleId, memberUserId, channelType }) => {
        const p = new URLSearchParams({ userId: memberUserId });
        if (channelType) p.set('channelType', channelType);
        return `/circles/${circleId}/provider-access?${p.toString()}`;
      },
      providesTags: (_res, _err, arg) => [
        {
          type: 'Circle' as const,
          id: `${arg.circleId}-provider-access-${arg.memberUserId}`,
        },
      ],
    }),

    grantMemberProviderAccess: builder.mutation<
      { ok: true; grant: { id: string } },
      {
        circleId: string;
        memberUserId: string;
        workspaceProviderId: string;
        channelType: string;
      }
    >({
      query: ({ circleId, memberUserId, workspaceProviderId, channelType }) => ({
        url: `/circles/${circleId}/provider-access`,
        method: 'POST',
        body: { userId: memberUserId, workspaceProviderId, channelType },
      }),
      invalidatesTags: (_res, _err, arg) => [
        { type: 'Circle', id: `${arg.circleId}-provider-access-${arg.memberUserId}` },
        { type: 'Circle', id: `${arg.circleId}-provider-access-me` },
      ],
    }),

    revokeMemberProviderAccess: builder.mutation<
      { ok: true; idempotent: boolean },
      { circleId: string; memberUserId: string; grantId: string }
    >({
      query: ({ circleId, grantId }) => ({
        url: `/circles/${circleId}/provider-access/${encodeURIComponent(grantId)}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_res, _err, arg) => [
        { type: 'Circle', id: `${arg.circleId}-provider-access-${arg.memberUserId}` },
        { type: 'Circle', id: `${arg.circleId}-provider-access-me` },
      ],
    }),

    // Single-member read for the Manage-modal initial state.
    getCircleMemberProviders: builder.query<
      { assignments: MemberProviderAssignment[] },
      { circleId: string; memberUserId: string }
    >({
      query: ({ circleId, memberUserId }) =>
        `/circles/${circleId}/people/${encodeURIComponent(memberUserId)}/providers`,
      providesTags: (_res, _err, arg) => [
        {
          type: 'Circle' as const,
          id: `${arg.circleId}-people-providers-${arg.memberUserId}`,
        },
      ],
    }),

    // Head-only. The backend gate (authorizeCircleWrite → Head-of-Team)
    // is authoritative — the modal is displayed only to Heads on the
    // web tab, but a non-Head who fires the request directly still 403s.
    putCircleMemberProvider: builder.mutation<
      { assignment: MemberProviderAssignmentRow },
      {
        circleId: string;
        memberUserId: string;
        channelType: MemberAssignmentChannel;
        outboundProviderId: string;
      }
    >({
      query: ({ circleId, memberUserId, channelType, outboundProviderId }) => ({
        url: `/circles/${circleId}/people/${encodeURIComponent(memberUserId)}/providers/${channelType}`,
        method: 'PUT',
        body: { outboundProviderId },
      }),
      invalidatesTags: (_res, _err, arg) => [
        { type: 'Circle', id: `${arg.circleId}-people-providers` },
        {
          type: 'Circle',
          id: `${arg.circleId}-people-providers-${arg.memberUserId}`,
        },
      ],
    }),

    deleteCircleMemberProvider: builder.mutation<
      { success: boolean },
      {
        circleId: string;
        memberUserId: string;
        channelType: MemberAssignmentChannel;
      }
    >({
      query: ({ circleId, memberUserId, channelType }) => ({
        url: `/circles/${circleId}/people/${encodeURIComponent(memberUserId)}/providers/${channelType}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_res, _err, arg) => [
        { type: 'Circle', id: `${arg.circleId}-people-providers` },
        {
          type: 'Circle',
          id: `${arg.circleId}-people-providers-${arg.memberUserId}`,
        },
      ],
    }),

    // Phase 12.8 — Circle-native voice-token mint. Server hard-derives
    // userId from `req.user.id`; body is empty. Response carries the
    // resolved outboundProviderId so the UI can display which provider
    // fired (pinned member row or workspace default).
    mintCircleVoiceToken: builder.mutation<
      {
        circleId: string;
        userId: string;
        channelType: 'voice';
        token: string;
        identity: string;
        expiresAt: string;
        twimlAppSid: string;
        fromNumber: string | null;
        recordingMode: string;
        outboundProviderId: string | null;
      },
      { circleId: string }
    >({
      query: ({ circleId }) => ({
        url: `/circles/${circleId}/voice/token`,
        method: 'POST',
      }),
    }),
  }),
});

export const {
  useGetCirclesQuery,
  useGetCircleQuery,
  useGetCircleMemberHistoryQuery,
  useGetCirclePeopleQuery,
  useGetCirclePeopleCandidatesQuery,
  useGetWorkspaceCentralTeamsQuery,
  useCreateCircleMutation,
  usePreviewSmartCircleMutation,
  useUpdateCircleMutation,
  useArchiveCircleMutation,
  useRestoreCircleMutation,
  useDeleteCircleMutation,
  useAddCircleMembersMutation,
  useRemoveCircleMemberMutation,
  useBulkRemoveCircleMembersMutation,
  useAddCircleCentralMemberMutation,
  useUpdateCircleCentralMemberMutation,
  useRemoveCircleCentralMemberMutation,
  useGetCircleProvidersQuery,
  useAddCircleProviderMutation,
  useRemoveCircleProviderMutation,
  useGetCirclePeopleProvidersQuery,
  useGetCircleMemberProvidersQuery,
  usePutCircleMemberProviderMutation,
  useDeleteCircleMemberProviderMutation,
  useMintCircleVoiceTokenMutation,
  // Phase A5.6 — Provider access (Central grants). Named distinctly from
  // the pin hooks above so a caller cannot reach for the wrong one.
  useGetMyProviderAccessQuery,
  useGetMemberProviderAccessQuery,
  useGrantMemberProviderAccessMutation,
  useRevokeMemberProviderAccessMutation,
} = circlesApi;

export type { CentralTeamSummary, WorkspaceCentralTeamsResponse };
